/**
 * /control/estados-cuenta · Listado + generar + detalle M-10
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Receipt, Plus, FileDown, Send, RefreshCw } from "lucide-react";
import { EC_ESTADOS, fmtMXN, fmtFecha } from "./_common";

export default function EstadosCuenta() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [fids, setFids] = useState([]);
    const [form, setForm] = useState({
        fideicomiso_id: "",
        anio: new Date().getFullYear(),
        mes: new Date().getMonth() || 12,
        motivo_version: "",
    });

    const load = async () => {
        const r = await api.get("/control/estados-cuenta");
        setItems(r.data || []);
    };
    const loadFids = async () => {
        try {
            const r = await api.get("/fideicomisos");
            const arr = Array.isArray(r.data) ? r.data : r.data.items || [];
            setFids(arr.map(f => ({ id: f.id, label: f.numero_fideicomiso || f.numero_interno || f.id })));
        } catch { setFids([]); }
    };
    useEffect(() => { load(); loadFids(); }, []);

    const generar = async () => {
        if (!form.fideicomiso_id) { toast.error("Selecciona fideicomiso"); return; }
        try {
            const r = await api.post("/control/estados-cuenta/generar", form);
            toast.success(`EC ${r.data.folio_estado_cuenta} · v${r.data.version} · ${r.data.dentro_sla_emision ? "SLA ok" : "SLA excedido"}`);
            setOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al generar");
        }
    };

    const descargarPdf = (id) =>
        window.open(`${api.defaults.baseURL}/control/estados-cuenta/${id}/pdf`, "_blank");

    const entregar = async (id) => {
        const parte = prompt("ID de parte receptora:");
        if (!parte) return;
        const medio = prompt("Medio (email/portal/oficina/mensajeria):", "email") || "email";
        try {
            await api.post(`/control/estados-cuenta/${id}/entregar`, {
                parte_id: parte, medio,
            });
            toast.success("Entrega registrada");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    return (
        <div className="space-y-6" data-testid="ec-list">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Estados de Cuenta Mensuales · M-10
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Emisión mensual con SLA ≤10 días hábiles tras corte · Versiones incrementales si hay correcciones.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={load} data-testid="ec-refresh">
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                                data-testid="ec-nuevo">
                                <Plus className="h-4 w-4 mr-1" /> Generar
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>Generar estado de cuenta</DialogTitle>
                                <DialogDescription>
                                    Si ya existe para el periodo, se emite una versión incremental.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-semibold">Fideicomiso</label>
                                    <Select value={form.fideicomiso_id}
                                        onValueChange={v => setForm({ ...form, fideicomiso_id: v })}>
                                        <SelectTrigger data-testid="ec-sel-fid"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                                        <SelectContent>
                                            {fids.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-semibold">Año</label>
                                        <Input type="number" value={form.anio}
                                            onChange={e => setForm({ ...form, anio: Number(e.target.value) })} />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold">Mes</label>
                                        <Input type="number" min="1" max="12" value={form.mes}
                                            onChange={e => setForm({ ...form, mes: Number(e.target.value) })} />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Motivo de versión (opcional)</label>
                                    <Input value={form.motivo_version}
                                        onChange={e => setForm({ ...form, motivo_version: e.target.value })}
                                        placeholder="Corrección contable / inclusión mov. tardío…" />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                                <Button onClick={generar} data-testid="ec-generar-btn">Generar</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm" data-testid="ec-table">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Folio</th>
                                <th className="px-3 py-2 text-left">Periodo</th>
                                <th className="px-3 py-2 text-left">Versión</th>
                                <th className="px-3 py-2 text-left">SLA</th>
                                <th className="px-3 py-2 text-left">Saldo final</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                                <th className="px-3 py-2 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                                    Sin estados de cuenta. Genera el primero.
                                </td></tr>
                            )}
                            {items.map(ec => {
                                const cfg = EC_ESTADOS[ec.estado] || { label: ec.estado, cls: "" };
                                const saldo = ec.contenido?.resumen_patrimonio?.saldo_final;
                                return (
                                    <tr key={ec.id} className="border-t hover:bg-muted/20">
                                        <td className="px-3 py-2 font-mono text-xs">{ec.folio_estado_cuenta}</td>
                                        <td className="px-3 py-2">
                                            {String(ec.periodo?.mes).padStart(2, "0")}/{ec.periodo?.anio}
                                        </td>
                                        <td className="px-3 py-2">v{ec.version}</td>
                                        <td className="px-3 py-2 text-xs">
                                            {ec.dentro_sla_emision
                                                ? <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 border">cumplido</Badge>
                                                : <Badge className="bg-red-100 text-red-900 border-red-300 border">excedido</Badge>}
                                        </td>
                                        <td className="px-3 py-2 text-xs">{fmtMXN(saldo)}</td>
                                        <td className="px-3 py-2"><Badge className={cfg.cls}>{cfg.label}</Badge></td>
                                        <td className="px-3 py-2 text-right space-x-1">
                                            <Button size="sm" variant="outline" onClick={() => descargarPdf(ec.id)}
                                                data-testid={`ec-pdf-${ec.id}`}>
                                                <FileDown className="h-3 w-3" />
                                            </Button>
                                            {ec.estado === "generado" && (
                                                <Button size="sm" variant="outline" onClick={() => entregar(ec.id)}
                                                    data-testid={`ec-entregar-${ec.id}`}>
                                                    <Send className="h-3 w-3" />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}
